import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Arrow } from "../../assets/icons";
import "swiper/css";
import "swiper/css/navigation";
import "../../css/Common-components/horizontalPagedList.css";

// 내부에서만 관리하는 레이아웃 설정 (MO/TA/PC 구간 자유 설정)
const SPACE_BETWEEN_DEFAULT = 24;
const DEVICE_CONFIG = {
  // MO: 0~651px → 1개, spaceBetween 0
  mo: {
    spaceBetween: 0,
    breakpoints: {
      0: { slidesPerView: 1, slidesPerGroup: 1 },
    },
  },
  // TA: 652~949 → 2개, 950~1247 → 3개, 1248~1327 → 4개, spaceBetween 24
  ta: {
    spaceBetween: 24,
    breakpoints: {
      652: { slidesPerView: 2, slidesPerGroup: 2 },
      950: { slidesPerView: 3, slidesPerGroup: 3 },
      1248: { slidesPerView: 4, slidesPerGroup: 4 },
    },
  },
  // PC: ~1328px까지 4개, spaceBetween 24 (컨테이너 최대 폭 기준)
  pc: {
    spaceBetween: 24,
    breakpoints: {
      1328: { slidesPerView: 4, slidesPerGroup: 4 },
    },
  },
};

// 가로 스와이프 리스트: 뷰포트에 맞춰 슬라이드 수와 페이지네이션을 조정한다.
function HorizontalSwiperList({ data, renderItem }) {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  // 현재 브레이크포인트에서 한 페이지당 몇 개의 슬라이드를 보여주는지 계산한다.
  const updateTotalPages = (swiper) => {
    if (!swiper) return;
    const bp = swiper.params.breakpoints || {};
    const windowWidth = window.innerWidth;

    // 현재 적용된 slidesPerGroup 찾기 (없으면 slidesPerView 사용)
    let slidesPerGroup = swiper.params.slidesPerGroup || 1;
    Object.keys(bp)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((min) => {
        if (windowWidth >= min) {
          const conf = bp[min] || {};
          if (typeof conf.slidesPerGroup === 'number') {
            slidesPerGroup = conf.slidesPerGroup;
          } else if (typeof conf.slidesPerView === 'number') {
            slidesPerGroup = conf.slidesPerView;
          }
        }
      });

    const pages = Math.ceil((data?.length || 0) / Math.max(slidesPerGroup, 1));
    setTotalPages(isFinite(pages) && pages > 0 ? pages : 1);
  };

  // DEVICE_CONFIG → Swiper breakpoints 변환 (그룹별 spaceBetween 주입)
  const computedBreakpoints = useMemo(() => {
    const map = {};
    const addGroup = (group) => {
      if (!group || !group.breakpoints) return;
      const groupSpace = group.spaceBetween;
      Object.keys(group.breakpoints).forEach((min) => {
        const conf = { ...(group.breakpoints[min] || {}) };
        if (groupSpace != null && conf.spaceBetween == null) conf.spaceBetween = groupSpace;
        map[Number(min)] = conf;
      });
    };
    addGroup(DEVICE_CONFIG.mo);
    addGroup(DEVICE_CONFIG.ta);
    addGroup(DEVICE_CONFIG.pc);
    return map;
  }, []);

  useEffect(() => {
    // Swiper 인스턴스를 보유하게 되면 resize 이벤트마다 페이지 수를 재계산한다.
    if (swiperInstance) {
      updateTotalPages(swiperInstance);
      swiperInstance.on("resize", () => updateTotalPages(swiperInstance));
    }
  }, [swiperInstance, data.length]);

  // 이전 페이지 이동
  const goPrev = () => {
    if (swiperInstance && swiperInstance.slidePrev) {
      swiperInstance.slidePrev();
    }
  };

  // 다음 페이지 이동
  const goNext = () => {
    if (swiperInstance && swiperInstance.slideNext) {
      swiperInstance.slideNext();
    }
  };

  return (
    <div className="horizontal-swiper-wrapper">
      <Swiper
        modules={[Navigation]}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
          updateTotalPages(swiper);
        }}
        onSlideChange={(swiper) => {
          setCurrentPage(swiper.snapIndex); // 정확한 페이지 번호
        }}
        spaceBetween={SPACE_BETWEEN_DEFAULT}
        loop={false}
        loopFillGroupWithBlank={true}
        breakpoints={Object.keys(computedBreakpoints).length > 0 ? computedBreakpoints : {
          100: { slidesPerView: 1, slidesPerGroup: 1 },
          652: { slidesPerView: 2, slidesPerGroup: 2 },
          950: { slidesPerView: 3, slidesPerGroup: 3 },
          1180: { slidesPerView: 4, slidesPerGroup: 4 },
        }}
      >
        {data.map((item, i) => (
          <SwiperSlide key={i}>
            <div
              className="swiper-item"
              style={{ cursor: "pointer" }}
            >
              {renderItem(item, i)}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="swiper-custom-controls">
        <div className="swiper-custom-control-wrapper">
          <button onClick={goPrev} disabled={currentPage === 0} className="btn">
            <Arrow className="arrow-btn-icon" />
          </button>
          <div className="page-num">
            {Math.min(currentPage + 1, totalPages)}
          </div>
          <button onClick={goNext} disabled={currentPage >= totalPages - 1} className="btn">
            <Arrow className="arrow-btn-icon rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default HorizontalSwiperList;
